/**
 * Lucide-compatible Solar Linear icons.
 *
 * Call sites keep `from "lucide-react"`; Vite aliases that module here so the
 * dashboard chrome draws Solar (FNR icon set) without rewriting every import.
 */
import type { ComponentType, SVGProps } from "react";
import type { Icon as SolarIcon } from "@solar-icons/react-perf/lib/types";
import {
  AddCircle as SolarAddCircle,
  AddFolder as SolarAddFolder,
  AddSquare as SolarAddSquare,
  Alarm as SolarAlarm,
  AlarmTurnOff as SolarAlarmTurnOff,
  AltArrowDown as SolarAltArrowDown,
  AltArrowLeft as SolarAltArrowLeft,
  AltArrowRight as SolarAltArrowRight,
  AltArrowUp as SolarAltArrowUp,
  Archive as SolarArchive,
  ArchiveDown as SolarArchiveDown,
  ArrowDown as SolarArrowDown,
  ArrowLeft as SolarArrowLeft,
  ArrowLeftUp as SolarArrowLeftUp,
  ArrowRight as SolarArrowRight,
  ArrowRightUp as SolarArrowRightUp,
  ArrowUp as SolarArrowUp,
  BatteryFull as SolarBatteryFull,
  Book as SolarBook,
  Bookmark as SolarBookmark,
  Bolt as SolarBolt,
  Box as SolarBox,
  Buildings2 as SolarBuildings2,
  Bug as SolarBug,
  Calendar as SolarCalendar,
  Camera as SolarCamera,
  Card as SolarCard,
  Case as SolarCase,
  Chart as SolarChart,
  Chart2 as SolarChart2,
  ChatRound as SolarChatRound,
  ChatRoundLine as SolarChatRoundLine,
  ChatSquare as SolarChatSquare,
  ChatSquare2 as SolarChatSquare2,
  ChatSquareArrow as SolarChatSquareArrow,
  CheckCircle as SolarCheckCircle,
  CheckRead as SolarCheckRead,
  ClockCircle as SolarClockCircle,
  CloseCircle as SolarCloseCircle,
  Cloud as SolarCloud,
  CloudDownload as SolarCloudDownload,
  CloudUpload as SolarCloudUpload,
  Code as SolarCode,
  Code2 as SolarCode2,
  CodeFile as SolarCodeFile,
  Copy as SolarCopy,
  Cpu as SolarCpu,
  Cursor as SolarCursor,
  DangerCircle as SolarDangerCircle,
  DangerTriangle as SolarDangerTriangle,
  Database as SolarDatabase,
  Dialog as SolarDialog,
  Document as SolarDocument,
  Documents as SolarDocuments,
  DocumentText as SolarDocumentText,
  Download as SolarDownload,
  DoubleAltArrowDown as SolarDoubleAltArrowDown,
  DoubleAltArrowUp as SolarDoubleAltArrowUp,
  Eye as SolarEye,
  EyeClosed as SolarEyeClosed,
  FaceScanSquare as SolarFaceScanSquare,
  File as SolarFile,
  FileText as SolarFileText,
  Filter as SolarFilter,
  Folder as SolarFolder,
  FolderOpen as SolarFolderOpen,
  FolderWithFiles as SolarFolderWithFiles,
  Forbidden as SolarForbidden,
  FullScreen as SolarFullScreen,
  Ghost as SolarGhost,
  Global as SolarGlobal,
  Gallery as SolarGallery,
  GalleryAdd as SolarGalleryAdd,
  Hashtag as SolarHashtag,
  History as SolarHistory,
  InfoCircle as SolarInfoCircle,
  Keyboard as SolarKeyboard,
  Laptop as SolarLaptop,
  Layers as SolarLayers,
  Letter as SolarLetter,
  Lightbulb as SolarLightbulb,
  Link as SolarLink,
  LinkBroken as SolarLinkBroken,
  LinkCircle as SolarLinkCircle,
  List as SolarList,
  ListCheck as SolarListCheck,
  Lock as SolarLock,
  LockUnlocked as SolarLockUnlocked,
  Login as SolarLogin,
  Magnifer as SolarMagnifer,
  Maximize as SolarMaximize,
  MenuDots as SolarMenuDots,
  Microphone as SolarMicrophone,
  PhoneRounded as SolarPhoneRounded,
  Soundwave as SolarSoundwave,
  MinusCircle as SolarMinusCircle,
  Monitor as SolarMonitor,
  Moon as SolarMoon,
  MoveToFolder as SolarMoveToFolder,
  MusicNote as SolarMusicNote,
  PaintRoller as SolarPaintRoller,
  Palette as SolarPalette,
  Paperclip as SolarPaperclip,
  Pen as SolarPen,
  Pen2 as SolarPen2,
  PenNewSquare as SolarPenNewSquare,
  Pin as SolarPin,
  Pipette as SolarPipette,
  Plain as SolarPlain,
  Play as SolarPlay,
  PlugCircle as SolarPlugCircle,
  PresentationGraph as SolarPresentationGraph,
  Programming as SolarProgramming,
  QuestionCircle as SolarQuestionCircle,
  QuitFullScreen as SolarQuitFullScreen,
  QuitPip as SolarQuitPip,
  Radar as SolarRadar,
  Record as SolarRecord,
  RecordCircle as SolarRecordCircle,
  Refresh as SolarRefresh,
  Repeat as SolarRepeat,
  Restart as SolarRestart,
  RoundGraph as SolarRoundGraph,
  RulerPen as SolarRulerPen,
  SafeSquare as SolarSafeSquare,
  Server as SolarServer,
  Settings as SolarSettings,
  SettingsMinimalistic as SolarSettingsMinimalistic,
  Share as SolarShare,
  ShieldCheck as SolarShieldCheck,
  Sidebar as SolarSidebar,
  SidebarMinimalistic as SolarSidebarMinimalistic,
  SlashCircle as SolarSlashCircle,
  SliderVertical as SolarSliderVertical,
  Smartphone as SolarSmartphone,
  SmileCircle as SolarSmileCircle,
  SortVertical as SolarSortVertical,
  SquareTopDown as SolarSquareTopDown,
  Star as SolarStar,
  Stars as SolarStars,
  Structure as SolarStructure,
  Sun as SolarSun,
  Tag as SolarTag,
  Text as SolarText,
  TextField as SolarTextField,
  TrashBinTrash as SolarTrashBinTrash,
  Tuning as SolarTuning,
  UndoLeft as SolarUndoLeft,
  Upload as SolarUpload,
  UserPlus as SolarUserPlus,
  UserRounded as SolarUserRounded,
  UsersGroupRounded as SolarUsersGroupRounded,
  Videocamera as SolarVideocamera,
  Widget as SolarWidget,
  WiFiRouter as SolarWiFiRouter,
  WindowFrame as SolarWindowFrame,
} from "@solar-icons/react-perf/Linear";

type IconSvgProps = SVGProps<SVGSVGElement> & {
  className?: string;
  size?: number | string;
  color?: string;
  strokeWidth?: number;
  absoluteStrokeWidth?: boolean;
};

export type LucideIcon = ComponentType<IconSvgProps>;

function wrap(Icon: SolarIcon, mirrored = false): LucideIcon {
  function SolarLucideIcon({ className, size, color }: IconSvgProps) {
    return (
      <Icon
        data-solar=""
        className={className}
        size={size ?? "1em"}
        color={color ?? "currentColor"}
        mirrored={mirrored}
      />
    );
  }
  return SolarLucideIcon;
}

export const ActivityIcon = wrap(SolarChart);
export const AlarmClockIcon = wrap(SolarAlarm);
export const AlarmClockOffIcon = wrap(SolarAlarmTurnOff);
export const AlertTriangleIcon = wrap(SolarDangerTriangle);
export const ArchiveIcon = wrap(SolarArchive);
export const ArchiveX = wrap(SolarArchiveDown);
export const ArrowDownIcon = wrap(SolarArrowDown);
export const ArrowDownUpIcon = wrap(SolarSortVertical);
export const ArrowLeft = wrap(SolarArrowLeft);
export const ArrowLeftIcon = wrap(SolarArrowLeft);
export const ArrowRight = wrap(SolarArrowRight);
export const ArrowRightIcon = wrap(SolarArrowRight);
export const ArrowUpCircleIcon = wrap(SolarAltArrowUp);
export const ArrowUpDownIcon = wrap(SolarSortVertical);
export const ArrowUpIcon = wrap(SolarArrowUp);
export const ArrowUpRightIcon = wrap(SolarArrowRightUp);
export const BatteryIcon = wrap(SolarBatteryFull);
export const BookOpenIcon = wrap(SolarBook);
export const BookmarkIcon = wrap(SolarBookmark);
export const BriefcaseIcon = wrap(SolarCase);
export const BrainIcon: LucideIcon = function BrainIcon({ className, size, color }: IconSvgProps) {
  return (
    <svg
      data-solar=""
      aria-hidden="true"
      className={className}
      width={size ?? "1em"}
      height={size ?? "1em"}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color ?? "currentColor"}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 5a3 3 0 1 0-5.997.125 4 4 0 0 0-2.526 5.77 4 4 0 0 0 .556 6.588A4 4 0 1 0 12 18Z" />
      <path d="M12 5a3 3 0 1 1 5.997.125 4 4 0 0 1 2.526 5.77 4 4 0 0 1-.556 6.588A4 4 0 1 1 12 18Z" />
      <path d="M15 13a4.5 4.5 0 0 1-3-4 4.5 4.5 0 0 1-3 4" />
      <path d="M17.599 6.5a3 3 0 0 0 .399-1.375" />
      <path d="M6.003 5.125A3 3 0 0 0 6.401 6.5" />
      <path d="M3.477 10.896a4 4 0 0 1 .585-.396" />
      <path d="M19.938 10.5a4 4 0 0 1 .585.396" />
      <path d="M6 18a4 4 0 0 1-1.967-.516" />
      <path d="M19.967 17.484A4 4 0 0 1 18 18" />
    </svg>
  );
};
export const Bot = wrap(SolarGhost);
export const BotIcon = wrap(SolarGhost);
export const Braces = wrap(SolarCode);
export const BugIcon = wrap(SolarBug);
export const CalendarDaysIcon = wrap(SolarCalendar);
export const CalendarIcon = wrap(SolarCalendar);
export const Camera = wrap(SolarCamera);
export const ChartNoAxesColumnIcon = wrap(SolarChart2);
export const ChatSquare2Icon = wrap(SolarChatSquare2);
export const Check = wrap(SolarCheckRead);
export const CheckCircle2Icon = wrap(SolarCheckCircle);
export const CheckIcon = wrap(SolarCheckRead);
export const ChevronDown = wrap(SolarAltArrowDown);
export const ChevronDownIcon = wrap(SolarAltArrowDown);
export const ChevronLeftIcon = wrap(SolarAltArrowLeft);
export const ChevronRight = wrap(SolarAltArrowRight);
export const ChevronRightIcon = wrap(SolarAltArrowRight);
export const ChevronUpIcon = wrap(SolarAltArrowUp);
export const ChevronsDownUpIcon = wrap(SolarDoubleAltArrowDown);
export const ChevronsUpDownIcon = wrap(SolarDoubleAltArrowUp);
export const CircleAlert = wrap(SolarDangerCircle);
export const CircleAlertIcon = wrap(SolarDangerCircle);
export const CircleCheckIcon = wrap(SolarCheckCircle);
export const CircleDashedIcon = wrap(SolarRecordCircle);
export const CircleDotIcon = wrap(SolarRecord);
export const CircleIcon = wrap(SolarRecordCircle);
export const CircleSlashIcon = wrap(SolarSlashCircle);
export const CircleXIcon = wrap(SolarCloseCircle);
export const ClockIcon = wrap(SolarClockCircle);
export const CloudDownloadIcon = wrap(SolarCloudDownload);
export const CloudIcon = wrap(SolarCloud);
export const CloudUploadIcon = wrap(SolarCloudUpload);
export const Code2 = wrap(SolarCode2);
export const Columns2Icon = wrap(SolarSliderVertical);
export const BoxIcon = wrap(SolarBox);
export const BoxesIcon = wrap(SolarBox);
export const ContainerIcon = wrap(SolarBox);
export const ClipboardListIcon = wrap(SolarListCheck);
export const CopyIcon = wrap(SolarCopy);
export const CreditCardIcon = wrap(SolarCard);
export const CornerLeftUpIcon = wrap(SolarArrowLeftUp);
export const CpuIcon = wrap(SolarCpu);
export const DatabaseIcon = wrap(SolarDatabase);
export const DownloadIcon = wrap(SolarDownload);
export const EllipsisIcon = wrap(SolarMenuDots);
export const ExternalLink = wrap(SolarSquareTopDown);
export const ExternalLinkIcon = wrap(SolarSquareTopDown);
export const Eye = wrap(SolarEye);
export const EyeIcon = wrap(SolarEye);
export const EyeOffIcon = wrap(SolarEyeClosed);
export const FileAudioIcon = wrap(SolarMusicNote);
export const FileCode2Icon = wrap(SolarCodeFile);
export const FileDiff = wrap(SolarDocument);
export const FileDiffIcon = wrap(SolarDocument);
export const FileIcon = wrap(SolarFile);
export const FileImageIcon = wrap(SolarGallery);
export const FileJsonIcon = wrap(SolarCodeFile);
export const FileSearchIcon = wrap(SolarMagnifer);
export const FileTextIcon = wrap(SolarFileText);
export const FileVideoIcon = wrap(SolarVideocamera);
export const Files = wrap(SolarDocuments);
export const FlaskConicalIcon = wrap(SolarTuning);
export const FolderClosedIcon = wrap(SolarFolder);
export const FolderGit2Icon = wrap(SolarFolderWithFiles);
export const FolderGitIcon = wrap(SolarFolderWithFiles);
export const FolderIcon = wrap(SolarFolder);
export const FolderInputIcon = wrap(SolarMoveToFolder);
export const FolderOpenIcon = wrap(SolarFolderOpen);
export const FolderPlusIcon = wrap(SolarAddFolder);
export const FolderTree = wrap(SolarStructure);
export const Frame = wrap(SolarMaximize);
export const GaugeIcon = wrap(SolarRadar);
export const GitBranchIcon = wrap(SolarShare);
export const GitBranchPlusIcon = wrap(SolarShare);
export const GitCommitHorizontalIcon = wrap(SolarRecord);
export const GitCommitIcon = wrap(SolarRecord);
export const GitMergeIcon = wrap(SolarShare);
export const GitPullRequest = wrap(SolarChatSquareArrow);
export const GitPullRequestClosedIcon = wrap(SolarCloseCircle);
export const GitPullRequestDraftIcon = wrap(SolarDocument);
export const GitPullRequestIcon = wrap(SolarChatSquareArrow);
export const GithubIcon = wrap(SolarProgramming);
export const Globe = wrap(SolarGlobal);
export const Globe2 = wrap(SolarGlobal);
export const Globe2Icon = wrap(SolarGlobal);
export const GlobeIcon = wrap(SolarGlobal);
export const HammerIcon = wrap(SolarTuning);
export const HardDriveIcon = wrap(SolarServer);
export const HashIcon = wrap(SolarHashtag);
export const History = wrap(SolarHistory);
export const HistoryIcon = wrap(SolarHistory);
export const ImageIcon = wrap(SolarGallery);
export const ImagePlusIcon = wrap(SolarGalleryAdd);
export const InfoIcon = wrap(SolarInfoCircle);
export const KeyboardIcon = wrap(SolarKeyboard);
export const LaptopIcon = wrap(SolarLaptop);
export const LayersIcon = wrap(SolarLayers);
export const LayoutGridIcon = wrap(SolarWidget);
export const LightbulbIcon = wrap(SolarLightbulb);
export const Link2 = wrap(SolarLink);
export const Link2Icon = wrap(SolarLink);
export const LinkIcon = wrap(SolarLinkCircle);
export const ListIcon = wrap(SolarList);
export const ListChecksIcon = wrap(SolarListCheck);
export const ListFilterIcon = wrap(SolarFilter);
export const Loader2Icon = wrap(SolarRefresh);
export const LoaderCircle = wrap(SolarRefresh);
export const LoaderCircleIcon = wrap(SolarRefresh);
export const LoaderIcon = wrap(SolarRefresh);
export const LockIcon = wrap(SolarLock);
export const LockOpenIcon = wrap(SolarLockUnlocked);
export const LogInIcon = wrap(SolarLogin);
export const MailIcon = wrap(SolarLetter);
export const Maximize2Icon = wrap(SolarFullScreen);
export const MemoryStickIcon = wrap(SolarServer);
export const MessageCircle = wrap(SolarChatRound);
export const MessageCircleIcon = wrap(SolarChatRound);
export const MessageCircleQuestionIcon = wrap(SolarQuestionCircle);
export const MessageSquareIcon = wrap(SolarChatSquare);
export const MessageSquareOffIcon = wrap(SolarForbidden);
export const MessageSquareTextIcon = wrap(SolarChatSquare2);
export const MessageSquareWarningIcon = wrap(SolarChatRoundLine);
export const MessagesSquare = wrap(SolarDialog);
export const MessagesSquareIcon = wrap(SolarDialog);
export const Mic = wrap(SolarMicrophone);
export const MicIcon = wrap(SolarMicrophone);
export const Phone = wrap(SolarPhoneRounded);
export const PhoneIcon = wrap(SolarPhoneRounded);
export const AudioLines = wrap(SolarSoundwave);
export const AudioLinesIcon = wrap(SolarSoundwave);
export const Minimize2Icon = wrap(SolarQuitFullScreen);
export const Minus = wrap(SolarMinusCircle);
export const MinusIcon = wrap(SolarMinusCircle);
export const MonitorIcon = wrap(SolarMonitor);
export const MoonIcon = wrap(SolarMoon);
export const MoreHorizontalIcon = wrap(SolarMenuDots);
export const MoreVertical = wrap(SolarMenuDots);
export const MousePointer2 = wrap(SolarCursor);
export const MousePointer2Icon = wrap(SolarCursor);
export const MousePointerClick = wrap(SolarCursor);
export const MousePointerClickIcon = wrap(SolarCursor);
export const OctagonAlertIcon = wrap(SolarDangerTriangle);
export const Paintbrush = wrap(SolarPaintRoller);
export const PaintbrushIcon = wrap(SolarPaintRoller);
export const PaletteIcon = wrap(SolarPalette);
export const PanelBottomIcon = wrap(SolarSidebarMinimalistic);
export const PanelLeftCloseIcon = wrap(SolarSidebar, true);
export const PanelLeftIcon = wrap(SolarSidebar);
export const PanelRightIcon = wrap(SolarSidebarMinimalistic);
export const PaperclipIcon = wrap(SolarPaperclip);
export const PenLine = wrap(SolarPen);
export const PenLineIcon = wrap(SolarPen);
export const PencilIcon = wrap(SolarPen2);
export const PencilRulerIcon = wrap(SolarRulerPen);
export const PictureInPicture2 = wrap(SolarQuitPip);
export const PilcrowIcon = wrap(SolarText);
export const PinIcon = wrap(SolarPin);
export const PipetteIcon = wrap(SolarPipette);
export const PlayIcon = wrap(SolarPlay);
export const PlugIcon = wrap(SolarPlugCircle);
export const PresentationIcon = wrap(SolarPresentationGraph);
export const Plus = wrap(SolarAddCircle);
export const PlusIcon = wrap(SolarAddSquare);
export const RadioTower = wrap(SolarRadar);
export const ReceiptIcon = wrap(SolarDocumentText);
export const RefreshCwIcon = wrap(SolarRefresh);
export const Repeat = wrap(SolarRepeat);
export const RepeatIcon = wrap(SolarRepeat);
export const RotateCcw = wrap(SolarUndoLeft);
export const RotateCcwIcon = wrap(SolarUndoLeft);
export const RotateCw = wrap(SolarRestart);
export const RotateCwIcon = wrap(SolarRestart);
export const Rows3Icon = wrap(SolarListCheck);
export const SearchIcon = wrap(SolarMagnifer);
export const SendIcon = wrap(SolarPlain);
export const ServerIcon = wrap(SolarServer);
export const Settings2Icon = wrap(SolarSettingsMinimalistic);
export const SettingsIcon = wrap(SolarSettings);
export const ShieldCheckIcon = wrap(SolarShieldCheck);
export const SmartphoneIcon = wrap(SolarSmartphone);
export const SmilePlusIcon = wrap(SolarSmileCircle);
export const RocketIcon = wrap(SolarStars);
export const SparklesIcon = wrap(SolarStars);
export const SquarePenIcon = wrap(SolarPenNewSquare);
export const SquareSplitHorizontal = wrap(SolarSliderVertical);
export const SquareSplitVertical = wrap(SolarSidebar);
export const StarIcon = wrap(SolarStar);
export const SunIcon = wrap(SolarSun);
export const Table2Icon = wrap(SolarWidget);
export const TagIcon = wrap(SolarTag);
export const TerminalIcon = wrap(SolarWindowFrame);
export const TerminalSquare = wrap(SolarWindowFrame);
export const TextSearchIcon = wrap(SolarMagnifer);
export const TextWrapIcon = wrap(SolarTextField);
export const Trash2 = wrap(SolarTrashBinTrash);
export const TrendingUpIcon = wrap(SolarChart);
export const Trash2Icon = wrap(SolarTrashBinTrash);
export const TriangleAlertIcon = wrap(SolarDangerTriangle);
export const Undo2Icon = wrap(SolarUndoLeft);
export const Unlink2 = wrap(SolarLinkBroken);
export const UploadIcon = wrap(SolarUpload);
export const UserPlusIcon = wrap(SolarUserPlus);
export const UserRoundIcon = wrap(SolarUserRounded);
export const UsersIcon = wrap(SolarUsersGroupRounded);
export const UsersRoundIcon = wrap(SolarUsersGroupRounded);
export const VaultIcon = wrap(SolarSafeSquare);
export const WalletIcon = wrap(SolarSafeSquare);
export const WifiOffIcon = wrap(SolarWiFiRouter);
export const WrapTextIcon = wrap(SolarTextField);
export const WrenchIcon = wrap(SolarTuning);
export const X = wrap(SolarCloseCircle);
export const XCircleIcon = wrap(SolarCloseCircle);
export const XIcon = wrap(SolarCloseCircle);
export const ZapIcon = wrap(SolarBolt);

/** Morrow workspace nav + page chrome */
export const AgentsIcon = wrap(SolarFaceScanSquare);
export const KnowledgeIcon = wrap(SolarRoundGraph);
export const OrganizationIcon = wrap(SolarBuildings2);
